import { RE_LINEA_TRAYECTORIA } from './trayectoria';

// Presentación determinista: mueve líneas completas, sin reinterpretar hechos.
const EXAMEN = /^Al examen (?:f[ií]sico|de control)\b/i;
const RELACION_TEMPORAL = /\b(?:luego|después|posteriormente|antes de|tras lo cual|inicialmente|al inicio|en el control)\b|\d{1,2}:\d{2}|\d{4}-\d{2}-\d{2}|(?:^|\n|\b(?:el|del|fecha) )\s*\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b/i;

// Solo presentación: esta prosa no equivale a la nota exacta de recepción
// que puede sustituirse con un dato vigente. Se mueve el renglón entero,
// incluida su procedencia; cualquier otro hecho o momento lo deja en origen.
const RIESGO_EN_PROSA = '(?:Braden con riesgo (?:muy alto|alto|moderado|bajo)|Downton con riesgo (?:alto|bajo)(?: de ca[ií]das)?)(?: \\(recibido del turno anterior\\))?';
const LINEA_RIESGOS_EN_PROSA = new RegExp(`^${RIESGO_EN_PROSA}(?:\\.\\s+${RIESGO_EN_PROSA})*\\.?$`, 'i');

export function esLineaRiesgosParaSeguridad(texto) {
  const linea = String(texto || '').trim();
  return !/[\r\n]/.test(linea) && LINEA_RIESGOS_EN_PROSA.test(linea);
}

// Orden clínico de párrafos completos. No partir por comas ni reescribir
// puntajes: el calificativo y la negación viajan con su propia observación.
// Si hay una relación temporal explícita entre renglones, conservar el relato.
export function ordenarNarrativaNeurologica(texto) {
  const src = String(texto || '');
  if (RELACION_TEMPORAL.test(src)) return src;
  const prioridad = linea => {
    if (EXAMEN.test(linea)) return 9;
    if (/\b(?:Glasgow|FOUR|RASS|SAS|vigil|sopor|somnol|conciencia|conexión con el medio|sin sedación|sedado|sedada)\b/i.test(linea)) return 0;
    if (/\borientad[oa]\b/i.test(linea)) return 1;
    if (/\b(?:CAM(?:-ICU)?|delirium)\b/i.test(linea)) return 2;
    if (/\b(?:dolor|EVA|CPOT|analgesia)\b/i.test(linea)) return 3;
    if (/\b(?:plejia|paresia|movilidad|mueve|motor|motora|reposo)\b/i.test(linea)) return 4;
    if (/\b(?:contenid[oa]s?|contenci[oó]n|contenciones)\b/i.test(linea)) return 5;
    return 6;
  };
  const lineas = src.split('\n').filter(l => l.trim());
  return lineas.map((linea, i) => ({ linea, i, p: prioridad(linea.trim()) }))
    .sort((a, b) => a.p - b.p || a.i - b.i).map(x => x.linea).join('\n');
}

// Solo consolida prefijos y copias literales de una misma valoración.
// No divide por comas: una negación, una dosis o un sitio pueden depender
// de toda la cláusula. Los contextos distintos conservan su orden y etiqueta.
function consolidarExamen(lineas) {
  const grupos = [];
  for (const linea of lineas) {
    const m = linea.trim().match(/^(Al examen (?:f[ií]sico(?: al recibir| de control)?|de control)):\s*(.+)$/i);
    if (!m) {
      grupos.push({ literal: linea });
      continue;
    }
    const prefijo = m[1];
    const cuerpo = m[2].replace(/\.$/, '');
    const anterior = grupos.at(-1);
    const clave = prefijo.toLocaleLowerCase();
    // Conservar constataciones con referencias temporales explícitas.
    const temporal = /\d{1,2}:\d{2}|\d{1,2}\/\d{1,2}|\b(?:luego|antes|después|posterior|recibir|control|horas?)\b/i.test(cuerpo);
    if (anterior && anterior.clave === clave && !anterior.temporal && !temporal) {
      if (!anterior.cuerpos.includes(cuerpo)) anterior.cuerpos.push(cuerpo);
    } else {
      grupos.push({ clave, prefijo, cuerpos: [cuerpo], temporal });
    }
  }
  return grupos.map(g => g.literal !== undefined
    ? g.literal
    : g.prefijo + ': ' + g.cuerpos.join('; ') + '.');
}

export function examenAlCierre(texto, encabezados = []) {
  const headers = new Set(encabezados.map(h => h.toLocaleLowerCase()));
  const out = [];
  let bloque = [];
  const cerrar = () => {
    const examen = bloque.filter(l => EXAMEN.test(l.trim()));
    if (!examen.length) out.push(...bloque);
    else {
      const resto = bloque.filter(l => !EXAMEN.test(l.trim()));
      while (resto.length && !resto.at(-1).trim()) resto.pop();
      out.push(...resto, ...(resto.length ? [''] : []), ...consolidarExamen(examen));
    }
    bloque = [];
  };
  for (const l of String(texto || '').split('\n')) {
    if (headers.has(l.trim().replace(/[.:]$/, '').toLocaleLowerCase())) {
      cerrar();
      if (out.length && out.at(-1).trim()) out.push('');
      out.push(l);
    } else if (RE_LINEA_TRAYECTORIA.test(l.trim()) || RELACION_TEMPORAL.test(l)) {
      // Cada momento separa valoraciones: ningún examen puede cruzarlo ni
      // consolidarse con otro situado al otro lado de esta línea.
      cerrar();
      out.push(l);
    } else bloque.push(l);
  }
  cerrar();
  return out.join('\n').replace(/\n{3,}/g, '\n\n');
}

// Compacta la afirmación redundante sin inventar cuántas valoraciones hubo.
// Conserva el texto cuando tiene tiempos o una transición explícita.
export function compactarFiebreRepetida(texto) {
  const src = String(texto || '');
  if (/\d{1,2}:\d{2}|afebril|sin fiebre|cede|cedió|recib|constata|posterior/i.test(src)) return src;
  const inicios = [...src.matchAll(/(?:^|[.!?]\s+|\n)Febril(?=[,.])/gi)];
  if (inicios.length < 2) return src;
  let n = 0;
  const resultado = src.replace(/(^|[.!?]\s+|\n)Febril\.(?=\s|$)/gi, (todo, antes, pos) => {
    if (pos === inicios[0].index) return todo;
    n += 1;
    return antes;
  });
  if (!n) return src;
  return resultado.trim();
}
